'use client';

import React from 'react';
import { HeroCardComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';

interface HeroCardProps {
  component: HeroCardComponent;
}

export const HeroCard: React.FC<HeroCardProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);

  // Determine background based on variant or custom gradient
  // New warm, professional color palette inspired by Nibbles
  const getBackground = () => {
    if (props.gradient) {
      return `bg-gradient-to-br from-${props.gradient.from} to-${props.gradient.to}`;
    }
    
    switch (props.variant) {
      case 'primary':
        // Muted Sage - calm, natural learning
        return 'bg-gradient-to-br from-learn-sage-50 to-learn-sage-100';
      case 'success':
        // Growth Green - progress (Twygs brand connection)
        return 'bg-gradient-to-br from-green-50 to-green-100';
      case 'warning':
        // Muted Honey/Amber - important, gentle attention
        return 'bg-gradient-to-br from-learn-honey-50 to-learn-honey-100';
      case 'danger':
        // Keep red for errors (rarely used)
        return 'bg-gradient-to-br from-red-50 to-rose-100';
      case 'info':
        // Muted Sky Blue - facts, calm information
        return 'bg-gradient-to-br from-learn-sky-50 to-learn-sky-100';
      case 'highlight':
        // Muted Clay/Coral - key concepts, memorable moments
        return 'bg-gradient-to-br from-learn-clay-50 to-learn-coral-100';
      default:
        // Very soft neutral
        return 'bg-gradient-to-br from-learn-sage-50 to-stone-50';
    }
  };

  // Border color based on variant - muted, pastel tones
  const getBorderColor = () => {
    switch (props.variant) {
      case 'primary':
        return 'border-learn-sage-300';
      case 'success':
        return 'border-green-300';
      case 'warning':
        return 'border-learn-honey-300';
      case 'danger':
        return 'border-red-300';
      case 'info':
        return 'border-learn-sky-300';
      case 'highlight':
        return 'border-learn-coral-300';
      default:
        return 'border-learn-sage-200';
    }
  };

  const animationClass = isVisible
    ? props.animation === 'fade-in'
      ? 'animate-fadeIn'
      : props.animation === 'slide-up'
      ? 'animate-slideUp'
      : props.animation === 'scale'
      ? 'animate-scaleIn'
      : ''
    : '';

  return (
    <div
      ref={ref}
      className={`w-full ${getBackground()} border ${getBorderColor()} rounded-2xl p-6 md:p-8 shadow-sm transition-all duration-300 ${animationClass}`}
      style={{ opacity: isVisible ? undefined : 0 }}
    >
      {/* Icon/Emoji + Title Section */}
      <div className="flex items-start gap-4 mb-5">
        {(props.icon || props.emoji) && (
          <div className="flex-shrink-0 mt-1">
            {props.emoji ? (
              <div className="text-4xl md:text-5xl opacity-90">{props.emoji}</div>
            ) : props.icon ? (
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white bg-opacity-60 rounded-xl flex items-center justify-center shadow-sm">
                <ComponentIcon icon={props.icon} className="w-7 h-7 md:w-8 md:h-8 text-gray-700" />
              </div>
            ) : null}
          </div>
        )}
        
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-1">
            {props.title}
          </h2>
          {props.subtitle && (
            <p className="text-sm md:text-base text-gray-600 mt-2 font-medium">
              {props.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        <p className="text-base md:text-lg text-gray-700 leading-relaxed">
          {props.content}
        </p>

        {/* Highlight Section - Elegant & Muted */}
        {props.highlight && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="bg-white bg-opacity-60 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-base md:text-lg font-semibold text-gray-800 leading-relaxed">
                {props.highlight}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

